

/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
    collapseItem,
    collapseIconBox,
    collapseIcon,
    collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";
import { useEffect, useState, useMemo } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import colors from "assets/theme/base/colors";
import solfyLogo from "assets/images/pearl-logo.png";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
    useMaterialUIController,
    setMiniSidenav,
    setTransparentSidenav,
    setWhiteSidenav,
} from "context";
import { useAuth } from "../../auth/Auth";
import { useTheme } from "@mui/material/styles";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
    const initialOpenSections = useMemo(() => {
        return routes
            .filter(route => !route.hideInSidenav && route.type === "title")
            .reduce((acc, route) => ({ ...acc, [route.key]: true }), {});
    }, [routes]);

    const [openSections, setOpenSections] = useState(() => {
        try {
            const saved = localStorage.getItem('sidenav_open_sections');
            return saved ? JSON.parse(saved) : initialOpenSections;
        } catch {
            return initialOpenSections;
        }
    });

    const toggleSection = (sectionKey) => {
        setOpenSections(prev => {
            const updated = { ...prev, [sectionKey]: !prev[sectionKey] };
            localStorage.setItem('sidenav_open_sections', JSON.stringify(updated));
            return updated;
        });
    };

    const { signout, license } = useAuth();
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
    const theme = useTheme();
    const location = useLocation();
    const collapseName = location.pathname.replace("/", "");
    // The plan badge. This used to read `localStorage.license`, a key nothing
    // writes -- and which auth/Auth.js actively removes on every login and on
    // every checkAuth bootstrap. The parse therefore always yielded {} and the
    // 'Trial' fallback always fired, so every tenant on every plan showed the
    // same gold TRIAL pill.
    //
    // The real value already arrives on /token and /verify as `license` and is
    // already on the auth context: license_type is plans.name for the active
    // subscription, resolved by get_seat_summary. Coming from context, it also
    // refreshes on the existing auth-token-updated event rather than being
    // frozen at mount by an empty dependency array.
    const licenseType = (license?.license_type || "").toUpperCase();

    // Trial-ness is a separate flag, not a plan named "Trial". `status` is
    // Active | Trial | Expiring Soon | Expired | Paused.
    const licenseStatus = license?.status || "";
    const badgeTone = useMemo(() => {
        if (licenseStatus === "Expired") return "expired";
        if (licenseStatus === "Expiring Soon") return "expiring";
        if (license?.is_trial || licenseStatus === "Trial") return "trial";
        return "paid";
    }, [license, licenseStatus]);

    const goldGradient =
        colors.brand?.goldGradient ||
        `linear-gradient(135deg, ${colors.warning.focus} 0%, ${colors.warning.main} 100%)`;

    // An expired or lapsing plan must not look identical to a healthy one --
    // that was the other half of the bug.
    const trialBadgeBg = useMemo(() => {
        if (badgeTone === "expired") return colors.error?.main || colors.error?.text;
        if (badgeTone === "paid") return colors.primary?.main || colors.brand?.primary;
        return goldGradient;
    }, [badgeTone, goldGradient]);

    const badgeTextColor = badgeTone === "trial" || badgeTone === "expiring" ? "dark" : "white";

    // Nothing to show until /verify has answered. An empty pill beats a wrong
    // one flashing on every page load.
    const showLicenseBadge = Boolean(licenseType);

    const closeSidenav = () => setMiniSidenav(dispatch, true);
    const handleSignOut = () => signout();

    useEffect(() => {
        function handleMiniSidenav() {
            setMiniSidenav(dispatch, window.innerWidth < 1200);
            setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
            setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
        }

        window.addEventListener("resize", handleMiniSidenav);
        handleMiniSidenav();
        return () => window.removeEventListener("resize", handleMiniSidenav);
    }, [dispatch]);

    let currentSectionKey = null;

    const renderRoutes = routes.filter(route => !route.hideInSidenav).map((route) => {
        const { type, name, icon, title, noCollapse, key, href, route: routePath, collapse } = route;

        // nested collapse (e.g. Travel)
        if (type === "collapse" && collapse) {
            const isOpen = openSections[key] !== false;

            return (
                <MDBox key={key}>
                    {/* Parent toggle — same structure as SidenavCollapse */}
                    <ListItem component="li" onClick={() => toggleSection(key)} sx={{ cursor: 'pointer', px: 1.5 }}>
                        <Tooltip title={name} placement="right" arrow enterDelay={300}>
                            <ListItem component="div" disablePadding>
                                <MDBox
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                    width="100%"
                                    sx={(theme) =>
                                        collapseItem(theme, {
                                            active: false,
                                            transparentSidenav,
                                            whiteSidenav,
                                            darkMode,
                                            sidenavColor,
                                        })
                                    }
                                >
                                    <ListItemIcon
                                        sx={(theme) => ({
                                            minWidth: 0,
                                            padding: 0,
                                            ...collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active: false }),
                                        })}
                                    >
                                        <MDBox sx={{ color: whiteSidenav ? theme.palette.primary.main : 'white', '& svg': { color: `${whiteSidenav ? theme.palette.primary.main : 'white'} !important` }, '& *': { color: `${whiteSidenav ? theme.palette.primary.main : 'white'} !important` } }}>
                                            {icon}
                                        </MDBox>
                                    </ListItemIcon>

                                    <ListItemText
                                        primary={name}
                                        primaryTypographyProps={{ align: "left", noWrap: true }}
                                        sx={(theme) => ({
                                            ...collapseText(theme, {
                                                miniSidenav,
                                                transparentSidenav,
                                                whiteSidenav,
                                                active: false,
                                                darkMode,
                                            }),
                                            flex: 1,
                                            color: whiteSidenav ? theme.palette.text.primary : 'rgba(255,255,255,0.88)',
                                            '& .MuiListItemText-primary': { color: `${whiteSidenav ? theme.palette.text.primary : 'rgba(255,255,255,0.88)'} !important` },
                                            '& .MuiTypography-root': { color: `${whiteSidenav ? theme.palette.text.primary : 'rgba(255,255,255,0.88)'} !important` },
                                        })}
                                    />

                                    {/* expand/collapse arrow */}
                                    <Icon sx={{ color: whiteSidenav ? theme.palette.text.secondary : 'rgba(255,255,255,0.5)', fontSize: '1rem !important', ml: 'auto' }}>
                                        {isOpen ? 'expand_less' : 'expand_more'}
                                    </Icon>
                                </MDBox>
                            </ListItem>
                        </Tooltip>
                    </ListItem>

                    {/* Child items — indented */}
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <MDBox sx={{ ml: 1.5, mr: 1, pl: 0.5 }}>
                            {collapse
                                .filter(child => !child.hideInSidenav)
                                .map(child => (
                                    <NavLink to={child.route} key={child.key} style={{ textDecoration: "none", display: "block" }}>
                                        <SidenavCollapse
                                            name={child.name}
                                            icon={child.icon}
                                            active={location.pathname === child.route}
                                            route={child.route}
                                        />
                                    </NavLink>
                                ))
                            }
                        </MDBox>
                    </Collapse>
                </MDBox>
            );
        }
        // title handling
        if (type === "title") {
            currentSectionKey = key;
            const isOpen = openSections[key] !== false;

            return (
                <MDBox key={key}>
                    <Tooltip title={title} placement="right" arrow enterDelay={300}>
                        <ListItemButton
                            onClick={() => toggleSection(key)}
                            sx={{
                                px: 2,
                                py: 0.75,
                                mx: 1.5,
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: whiteSidenav ? 'rgba(21, 101, 192, 0.06)' : 'rgba(255,255,255,0.08)' },
                            }}
                        >
                            <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                textTransform="uppercase"
                                sx={{
                                    color: whiteSidenav ? theme.palette.text.secondary : 'rgba(255,255,255,0.5)',
                                    fontSize: '0.65rem',
                                    letterSpacing: '1px',
                                    flexGrow: 1,
                                }}
                            >
                                {title}
                            </MDTypography>
                            <Icon sx={{ color: whiteSidenav ? theme.palette.text.secondary : 'rgba(255,255,255,0.5)', fontSize: '1rem !important' }}>
                                {isOpen ? 'expand_less' : 'expand_more'}
                            </Icon>
                        </ListItemButton>
                    </Tooltip>
                </MDBox>
            );
        }

        // regular collapse handling
        if (type === "collapse") {
            const isVisible = currentSectionKey === null || openSections[currentSectionKey] !== false;
            return (
                <Collapse key={key} in={isVisible} timeout="auto" unmountOnExit>
                    {href ? (
                        <Link href={href} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
                            <SidenavCollapse
                                name={name}
                                icon={icon}
                                active={key === collapseName}
                                noCollapse={noCollapse}
                            />
                        </Link>
                    ) : (
                        <NavLink to={routePath} style={{ textDecoration: "none", display: "block" }}>
                            <SidenavCollapse
                                name={name}
                                icon={icon}
                                active={key === collapseName}
                                route={routePath}
                            />
                        </NavLink>
                    )}
                </Collapse>
            );
        }

        // divider handling
        if (type === "divider") {
            return (
                <Divider
                    key={key}
                    light={!whiteSidenav}
                />
            );
        }

        return null;
    });

    return (
        <SidenavRoot
            {...rest}
            variant="permanent"
            ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
            sx={{
                background: 'transparent',
                color: whiteSidenav ? theme.palette.text.primary : 'rgba(255,255,255,0.88)',
                boxShadow: 'none',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <MDBox pt={3} pb={1} px={3} textAlign="center">
                <MDBox
                    display={{ xs: "block", xl: "none" }}
                    position="absolute"
                    top={0}
                    right={0}
                    p={1.625}
                    onClick={closeSidenav}
                    sx={{ cursor: "pointer" }}
                >
                    <MDTypography variant="h6" sx={{ color: whiteSidenav ? 'primary.main' : 'white.main' }}>
                        <Icon sx={{ fontWeight: "bold" }}>close</Icon>
                    </MDTypography>
                </MDBox>
                <MDBox component={NavLink} to="/" display="flex" flexDirection="column" alignItems="center" sx={{ textDecoration: "none" }}>
                    <MDBox
                        component="img"
                        src={solfyLogo}
                        alt=""
                        sx={{
                            height: miniSidenav ? 42 : 56,
                            width: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            mb: 1.5,
                        }}
                    />
                    {showLicenseBadge && (
                        <Tooltip
                            title={
                                license?.expiry_date
                                    ? `${licenseStatus || "Active"} \u00b7 renews ${new Date(
                                          license.expiry_date,
                                      ).toLocaleDateString()}`
                                    : licenseStatus || ""
                            }
                            placement="right"
                        >
                            <MDBox
                                sx={{
                                    background: trialBadgeBg,
                                    color:
                                        badgeTextColor === "white"
                                            ? theme.palette.white.main
                                            : theme.palette.dark.main,
                                    borderRadius: "999px",
                                    padding: "6px 20px",
                                    minWidth: "94px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 6px 14px rgba(17, 42, 82, 0.18)",
                                }}
                            >
                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.5px",
                                        textTransform: "uppercase",
                                        color: "inherit",
                                    }}
                                >
                                    {licenseType}
                                </MDTypography>
                            </MDBox>
                        </Tooltip>
                    )}
                </MDBox>
            </MDBox>
            <MDBox
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                        background: transparentSidenav
                            ? "rgba(255, 255, 255, 0.06)"
                            : whiteSidenav
                                ? "rgba(17, 42, 82, 0.1)"
                                : "rgba(255, 255, 255, 0.08)",
                        borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        background: transparentSidenav
                            ? "rgba(255, 255, 255, 0.14)"
                            : whiteSidenav
                                ? "rgba(17, 42, 82, 0.18)"
                                : "rgba(255, 255, 255, 0.16)",
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: transparentSidenav
                        ? "rgba(255, 255, 255, 0.06) transparent"
                        : whiteSidenav
                            ? "rgba(17, 42, 82, 0.1) transparent"
                            : "rgba(255, 255, 255, 0.08) transparent",
                }}
            >
            <List sx={{ px: 0.5, py: 1 }}>
                {renderRoutes}
            </List>
            </MDBox>
            <MDBox mt="auto" mb={2} mx={2}>
                <MDButton
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={handleSignOut}
                    sx={{
                        color: "#FFFFFF",
                        "& .MuiIcon-root": { color: "#FFFFFF" },
                    }}
            >
                    <Icon sx={{ mr: 1 }}>logout</Icon>
                    Sign Out
                </MDButton>
            </MDBox>
        </SidenavRoot>
    );
}

Sidenav.defaultProps = {
    color: "info",
    brand: "",
};

Sidenav.propTypes = {
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    brand: PropTypes.string,
    brandName: PropTypes.string.isRequired,
    routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
